// #region Imports
import React, { useEffect, useState } from 'react';
import './ComissoesTable.scss';
import iconSearch from '../../../assets/lupa.svg';
import iconEdit from '../../../assets/edit.svg';
import iconTrash from '../../../assets/trash.svg';
import iconCheck from '../../../assets/check.svg';
import { formatCurrency, formatDateISO } from '../../../../shared/helpers';
// #endregion

export default function ComissoesTable({
  loading = false,
  stats,
  filters,
  data = [],
  total = 0,
  page = 1,
  pageSize = 10,
  funcionarios = [],
  onFiltersChange = () => {},
  onPageChange = () => {},
  onCreate = () => {},
  onEdit = () => {},
  onDelete = () => {},
  onToggleStatus = () => {},
}) {
  // #region Estado
  const [draftFilters, setDraftFilters] = useState(filters);
  // #endregion

  // #region Efeitos
  useEffect(() => { setDraftFilters(filters); }, [filters]);
  // #endregion

  // #region Handlers – filtros & paginação
  function updateDraft(patch) {
    setDraftFilters(prev => ({ ...prev, ...patch }));
  }

  function applyFilters() { onFiltersChange(draftFilters); }

  function clearFilters() {
    const cleared = { q: '', funcionario_id: 'TODOS', status: 'TODOS', periodo: 'TODOS' };
    setDraftFilters(cleared);
    onFiltersChange(cleared);
  }

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  function jumpToPage(p) {
    const n = Math.min(Math.max(1, p), totalPages);
    onPageChange(n);
  }
  // #endregion

  // #region Render helpers
  function renderBody() {
    // Loading skeleton
    if (loading) {
      return Array.from({ length: 6 }).map((_, i) => (
        <tr key={`sk-${i}`} className="skeleton-row" aria-hidden="true">
          <td colSpan="7">
            <div className="sk-line w40" />
            <div className="sk-line w25" />
          </td>
        </tr>
      ));
    }

    // Empty state
    if (data.length === 0) {
      return (
        <>
          <tr>
            <td colSpan="7" className="empty-cell">Nenhuma comissão encontrada.</td>
          </tr>
          {Array.from({ length: 5 }).map((_, i) => (
            <tr key={`f-${i}`} className="row-filler" aria-hidden="true">
              <td colSpan="7" />
            </tr>
          ))}
        </>
      );
    }

    // Rows
    return (
      <>
        {data.map(c => {
          const funcionario = funcionarios.find(f => f.id === c.funcionario_id);
          return (
            <tr key={c.id} className={c.pago ? 'row-paid' : 'row-pending'}>
              <td>
                <div className="cell-main">
                  <span className="cell-title">{funcionario ? funcionario.nome : '—'}</span>
                  <span className="cell-sub">ID: {funcionario ? funcionario.id : '—'}</span>
                </div>
              </td>
              <td>#{c.venda_id}</td>
              <td />
              <td>{formatDateISO(c.data_competencia)}</td>
              <td className="valor">{formatCurrency(c.valor)}</td>
              <td>
                <span
                  className={`tag ${c.pago ? 'tag-success-outline' : 'tag-warning-outline'}`}
                >
                  {c.pago ? 'Pago' : 'Pendente'}
                </span>
              </td>
              <td className="actions">
                <button
                  type="button"
                  className={`icon-btn toggle-btn ${c.pago ? 'is-paid' : 'is-pending'}`}
                  title={c.pago ? 'Marcar como pendente' : 'Marcar como pago'}
                  onClick={() => onToggleStatus(c.id)}
                  aria-label={c.pago ? 'Marcar como pendente' : 'Marcar como pago'}
                >
                  <img src={iconCheck} alt="" className="icon-check" />
                </button>
                <button
                  type="button"
                  className="icon-btn"
                  title="Editar"
                  onClick={() => onEdit(c.id)}
                  aria-label="Editar comissão"
                >
                  <img src={iconEdit} alt="" />
                </button>
                <button
                  type="button"
                  className="icon-btn danger"
                  title="Excluir"
                  onClick={() => { if (window.confirm('Remover comissão?')) onDelete(c.id); }}
                  aria-label="Excluir comissão"
                >
                  <img src={iconTrash} alt="" />
                </button>
              </td>
            </tr>
          );
        })}
        {data.length < 6 && Array.from({ length: Math.max(0, 6 - data.length) }).map((_, i) => (
          <tr key={`fill-${i}`} className="row-filler" aria-hidden="true">
            <td colSpan="7" />
          </tr>
        ))}
      </>
    );
  }
  // #endregion

  // #region Render
  return (
    <div className="comissoes-module">
      {/* Header */}
      <div className="comissoes-head">
        <h2 className="comissoes-title">Comissões</h2>
        <button
          type="button"
          onClick={onCreate}
          className="btn btn-primary"
        >
          <span className="btn-icon">+</span>
          Nova Comissão
        </button>
      </div>

      {/* KPIs */}
      <div className="kpi-grid" role="region" aria-label="Indicadores de comissões">
        <div className="kpi-card">
          <span className="kpi-label">Total</span>
          <span className="kpi-value">{stats.total}</span>
        </div>
        <div className="kpi-card">
          <span className="kpi-label">Pagas</span>
          <span className="kpi-value">{stats.pagas}</span>
        </div>
        <div className="kpi-card">
          <span className="kpi-label">Pendentes</span>
          <span className="kpi-value">{stats.pendentes}</span>
        </div>
        <div className="kpi-card">
          <span className="kpi-label">Pago</span>
          <span className="kpi-value kpi-warning">{formatCurrency(stats.valorPago)}</span>
        </div>
        <div className="kpi-card">
          <span className="kpi-label">Pendente</span>
          <span className="kpi-value kpi-danger">{formatCurrency(stats.valorPendente)}</span>
        </div>
      </div>

      {/* Filter Panel */}
      <div className="filter-panel" role="form" aria-label="Filtros de comissões">
        <div className="filter-header">
          <img src={iconSearch} alt="" className="filter-icon" />
          <span className="filter-title">Filtros</span>
        </div>
        <div className="filter-grid">
          <div className="filter-field">
            <label htmlFor="f-q">Buscar</label>
            <input
              id="f-q"
              placeholder="Funcionário, cliente ou venda"
              value={draftFilters.q}
              onChange={e => updateDraft({ q: e.target.value })}
              disabled={loading}
            />
          </div>
          <div className="filter-field">
            <label htmlFor="f-func">Funcionário</label>
            <select
              id="f-func"
              value={draftFilters.funcionario_id}
              onChange={e => updateDraft({ funcionario_id: e.target.value })}
              disabled={loading}
            >
              <option value="TODOS">Todos</option>
              {funcionarios.map(f => (
                <option key={f.id} value={f.id}>{f.nome}</option>
              ))}
            </select>
          </div>
          <div className="filter-field">
            <label htmlFor="f-status">Status</label>
            <select
              id="f-status"
              value={draftFilters.status}
              onChange={e => updateDraft({ status: e.target.value })}
              disabled={loading}
            >
              <option value="TODOS">Todos</option>
              <option value="PAGAS">Pagas</option>
              <option value="PENDENTES">Pendentes</option>
            </select>
          </div>
            <div className="filter-field">
              <label htmlFor="f-periodo">Período</label>
              <select
                id="f-periodo"
                value={draftFilters.periodo}
                onChange={e => updateDraft({ periodo: e.target.value })}
                disabled={loading}
              >
                <option value="TODOS">Todos</option>
                <option value="2024-01">Jan/2024</option>
                <option value="2024-02">Fev/2024</option>
              </select>
            </div>
          <div className="filter-actions">
            <button
              type="button"
              className="btn btn-primary"
              onClick={applyFilters}
              disabled={loading}
            >
              Filtrar
            </button>
            <button
              type="button"
              className="btn btn-ghost"
              onClick={clearFilters}
              disabled={loading}
            >
              Limpar Filtros
            </button>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="table-wrapper" role="region" aria-label="Lista de comissões">
        <div className="table-container">
          <table className="ct-table">
            <thead>
              <tr>
                <th>Funcionário</th>
                <th>Venda</th>
                <th>Cliente</th>
                <th>Competência</th>
                <th>Valor</th>
                <th>Status</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>{renderBody()}</tbody>
          </table>
        </div>
        <div className="ct-footer" role="navigation" aria-label="Paginação">
          <div className="result-count">Total: {total}</div>
          <div className="pagination">
            <button
              type="button"
              onClick={() => jumpToPage(1)}
              disabled={page === 1 || loading}
            >
              &laquo;
            </button>
            <button
              type="button"
              onClick={() => jumpToPage(page - 1)}
              disabled={page === 1 || loading}
            >
              &lsaquo;
            </button>
            <label className="page-jump">
              <input
                value={String(page)}
                onChange={e => {
                  const v = e.target.value.replace(/\D/g, '');
                  if (v) jumpToPage(Number(v));
                }}
                disabled={loading}
              />
              <span className="total-pages">/ {totalPages}</span>
            </label>
            <button
              type="button"
              onClick={() => jumpToPage(page + 1)}
              disabled={page === totalPages || loading}
            >
              &rsaquo;
            </button>
            <button
              type="button"
              onClick={() => jumpToPage(totalPages)}
              disabled={page === totalPages || loading}
            >
              &raquo;
            </button>
          </div>
        </div>
      </div>
    </div>
  );
  // #endregion
}
